import {
	type AddressLookupResponse,
	type ShipmentLocationActionRequestDtoLocationActionType as LocationActionType,
	lookUpShippingLocation,
} from '@uturn/api/v1';
import { ShipmentDtoShipmentStatus as ShipmentStatus } from '@uturn/api/v2';
import { MetaDataContext } from '@uturn/portal/context';
import { ShipmentDetailContext } from '@uturn/portal/modules/shipments/providers/shipment-detail';
import { type FormValues } from '@uturn/portal/modules/shipments/schema';
import {
	fromZodLocations,
	guessTransportType,
	isShipmentMatched,
	transportTypeName,
} from '@uturn/portal/modules/shipments/utils';
import { type TransportTypeCode } from '@uturn/portal/types/shipment';
import { formatSuggestions } from '@uturn/portal/utils/format-address-lookup-suggestions';
import {
	Badge,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	sonner,
} from '@uturn/ui-kit';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AddShipmentLocation } from './add-shipment-location';
import { ShipmentLocation } from './shipment-location';

export type RouteSectionProps = {
	shipmentNumber?: number;
	shipmentStatus?: ShipmentStatus;
	isReadOnly?: boolean;
	transportType?: TransportTypeCode;
};

function getDateFrom(location: unknown): string | undefined {
	if (typeof location !== 'object' || !location) {
		return undefined;
	}
	if (!('action' in location)) {
		return undefined;
	}
	const { action } = location;
	if (typeof action !== 'object' || !action) {
		return undefined;
	}
	const dateFrom = 'dateFrom' in action ? action.dateFrom : undefined;
	return typeof dateFrom === 'string' && dateFrom ? dateFrom : undefined;
}

/**
 * Minimum selectable date for a location's pickers: the `dateFrom` of the
 * closest earlier location.
 */
function getFromDateForLocation(
	locations: unknown,
	index: number,
): Date | undefined {
	if (!Array.isArray(locations)) {
		return undefined;
	}
	for (let previous = index - 1; previous >= 0; previous -= 1) {
		const dateFrom = getDateFrom(locations[previous]);
		if (dateFrom) {
			return new Date(dateFrom);
		}
	}
	return undefined;
}

export function RouteSection({
	shipmentNumber,
	shipmentStatus = ShipmentStatus.UNKNOWN,
	isReadOnly = false,
	transportType,
}: RouteSectionProps) {
	const { t } = useTranslation();
	const { readOnlyFields } = useContext(ShipmentDetailContext);
	const { shipmentMetaData, shipmentMaxRoutes } = useContext(MetaDataContext);
	const form = useFormContext<FormValues>();
	const { fields, remove, insert } = useFieldArray({
		control: form.control,
		name: 'locations',
	});
	const watchedLocations = form.watch('locations');

	// Derive the transport type from the live locations so the badge stays in

	const liveTransportType =
		fields.length > 0
			? guessTransportType(fromZodLocations(fields).locationActions)
			: transportType;

	const [suggestions, setSuggestions] = useState<string[]>([]);
	const [autoCompleteValue, setAutoCompleteValue] = useState('');
	const [autoCompleteData, setAutoCompleteData] = useState<
		AddressLookupResponse[]
	>([]);

	const fetchAndRenderLocations = useCallback(async () => {
		if (!autoCompleteValue || autoCompleteValue === '') {
			setSuggestions([]);
			return;
		}

		const { data: shippingLocation } = await lookUpShippingLocation({
			searchText: autoCompleteValue,
		});

		setAutoCompleteData(shippingLocation);
		setSuggestions(formatSuggestions(shippingLocation));
	}, [autoCompleteValue, lookUpShippingLocation]);

	useEffect(() => {
		fetchAndRenderLocations();
	}, [fetchAndRenderLocations]);

	const insertLocation = (
		index: number,
		locationActionType: LocationActionType,
		isUpdatingShipment: boolean,
	) => {
		/**
		 * // TODO: Is the toast error below still needed, now the rules options
		 *          can be set when invoking useFieldArray?
		 * ```ts
		 *   const { fields, remove, insert } = useFieldArray({
		 *     control: form.control,
		 *     name: 'locations',
		 *     rules: { maxLength: shipmentMaxRoutes },
		 *   });
		 * ```
		 */
		if (fields.length >= shipmentMaxRoutes) {
			sonner.error(
				t(
					'pages.shipment.errors.max_routes_exceeded',
					'Maximum routes reached.',
				),
			);

			return;
		}
		/**
		 * When you append, prepend, insert and update the field array,
		 * the obj can't be empty object rather need to supply all your
		 * input's defaultValues.
		 *   append(); ❌
		 *   append({}); ❌
		 *   append({ firstName: 'bill', lastName: 'luo' }); ✅
		 *
		 * Check "Rules" in https://react-hook-form.com/docs/usefieldarray.
		 */
		if (isUpdatingShipment) {
			insert(index, {
				action: {
					id: crypto.randomUUID(),
					locationActionType,
					location: '',
					dateFrom: '',
					dateUntil: '',
					reference: '',
					remarks: '',
					customerName: '',
					shippingLineId: -1,
					shippingLine: {},
					port: '',
					vesselName: '',
					eta: '',
					arrival: '',
					departure: '',
					sequenceNumber: -1,
				},
				countryCode: '',
			});
		} else {
			insert(index, {
				action: {
					id: crypto.randomUUID(),
					locationActionType,
				},
			});
		}
	};

	return (
		<div className="relative h-full">
			<Card>
				<CardHeader>
					<CardTitle>
						<div className="flex">
							{t('pages.create_shipment.route.heading', 'Route')}
							{liveTransportType && (
								<Badge className="ml-auto align-top" variant="outline">
									{transportTypeName(t)[liveTransportType]}
								</Badge>
							)}
						</div>
					</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-col gap-6">
					<AnimatePresence>
						{fields.map((field: any, index: number) => {
							const locationActionId = field.action?.id ?? `${index + 1}`;
							const locationActionIdIsFromBE =
								typeof locationActionId === 'number';
							const locationFromDate = getFromDateForLocation(
								watchedLocations,
								index,
							);
							return (
								<motion.div
									key={field.id}
									initial={{ x: 100, opacity: 0 }}
									animate={{ x: 0, opacity: 1 }}
									exit={{ x: -100, opacity: 0 }}
								>
									<ShipmentLocation
										id={locationActionId}
										isReadOnly={isReadOnly}
										readOnlyFields={readOnlyFields}
										indexInLocations={index}
										shippingLines={shipmentMetaData?.shippingLines ?? []}
										autoCompleteData={autoCompleteData}
										setAutocompleteValue={setAutoCompleteValue}
										autocompleteSuggestions={suggestions}
										removeLocation={() => remove(index)}
										enablePortbase={
											!isShipmentMatched(shipmentStatus) &&
											locationActionIdIsFromBE
										}
										fromDate={locationFromDate}
									/>
									{index !== fields.length - 1 && !isReadOnly && (
										<AddShipmentLocation
											insert={(locationActionType) =>
												insertLocation(
													index + 1,
													locationActionType,
													locationActionIdIsFromBE,
												)
											}
										/>
									)}
								</motion.div>
							);
						})}
					</AnimatePresence>
				</CardContent>
			</Card>
		</div>
	);
}
